import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Button, Dialog, Checkbox, Select, MenuItem, RadioButton } from '@dsi/react-eds';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { AxiosError } from 'axios';
import { niiService } from '../../services/niiService';
import { enmService } from '../../services/enmService';
import { eppEnmService, EppEnm } from '../../services/eppEnmService';
import { eppConfig } from '../../config/eppConfig';
import { NiiRunType, EnmEndpoint, CpiRelease, CreateRunsRequest, UploadResponse } from './types/nii.types';
import collectEnmScriptUrl from '../../assets/scripts/collect_enm_data.sh?url';
import styles from './GenerateReportDialog.module.css';

interface GenerateReportDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

type DataSourceMode = 'enm_live' | 'enm_upload' | 'epp';

const GenerateReportDialog: React.FC<GenerateReportDialogProps> = ({ open, onClose, onSubmit }) => {
  const { addNotifications } = useSwobNotifications();

  // Form state
  const [selectedRelease, setSelectedRelease] = useState<CpiRelease | null>(null);
  const [availableReleases, setAvailableReleases] = useState<CpiRelease[]>([]);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [selectedRunTypes, setSelectedRunTypes] = useState<NiiRunType[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Data source mode
  const [dataSourceMode, setDataSourceMode] = useState<DataSourceMode>('enm_live');

  // ENM Live state
  const [enms, setEnms] = useState<EnmEndpoint[]>([]);
  const [selectedEnmIds, setSelectedEnmIds] = useState<string[]>([]);
  const [enmsLoading, setEnmsLoading] = useState(false);

  // Upload state
  const [enmFile, setEnmFile] = useState<File | null>(null);
  const [enmUploadResult, setEnmUploadResult] = useState<UploadResponse | null>(null);
  const [enmUploading, setEnmUploading] = useState(false);

  // EPP state (only used when EPP is enabled for this environment)
  const [eppEnms, setEppEnms] = useState<EppEnm[]>([]);
  const [selectedEppIds, setSelectedEppIds] = useState<string[]>([]);
  const [eppLoading, setEppLoading] = useState(false);

  // Fetch ENMs and available releases on dialog open
  useEffect(() => {
    if (open) {
      fetchEnms();
      fetchAvailableReleases();
      if (eppConfig.enabled) fetchEppEnms();
    } else {
      resetForm();
    }
  }, [open]);

  const fetchAvailableReleases = async () => {
    setReleasesLoading(true);
    try {
      const releases = await niiService.getCpiReleases();
      setAvailableReleases(releases);
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: error instanceof AxiosError ? error.response?.data?.message || error.message : 'Failed to load available releases.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setReleasesLoading(false);
    }
  };

  const fetchEnms = async () => {
    setEnmsLoading(true);
    try {
      const response = await enmService.getAll();
      const fetchedEnms: EnmEndpoint[] = response.data || [];
      setEnms(fetchedEnms);
      setSelectedEnmIds(fetchedEnms.map((e) => e.id));
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: error instanceof AxiosError ? error.response?.data?.message || error.message : 'Failed to load ENM endpoints.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setEnmsLoading(false);
    }
  };

  const fetchEppEnms = async () => {
    setEppLoading(true);
    try {
      const response = await eppEnmService.getAll();
      setEppEnms(response.data || []);
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: error instanceof AxiosError ? error.response?.data?.message || error.message : 'Failed to load EPP ENM endpoints.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setEppLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedRelease(null);
    setSelectedRunTypes([]);
    setDataSourceMode('enm_live');
    setSelectedEnmIds([]);
    setEnms([]);
    setEnmFile(null);
    setEnmUploadResult(null);
    setEppEnms([]);
    setSelectedEppIds([]);
    setSubmitting(false);
  };

  const handleRunTypeToggle = (type: NiiRunType) => {
    setSelectedRunTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  };

  const handleEnmFileUpload = async (file: File) => {
    setEnmFile(file);
    setEnmUploading(true);
    setEnmUploadResult(null);
    try {
      const result = await niiService.uploadFile(file, 'network_inventory');
      setEnmUploadResult(result);
      addNotifications([
        {
          title: 'Upload Success',
          icon: 'icon-check',
          description: `ENM data uploaded: ${result.validation?.featureCount ?? 0} features, ${result.validation?.hardwareCount ?? 0} hardware entries detected`,
          timestamp: new Date(),
          color: 'green'
        }
      ]);
    } catch (error) {
      addNotifications([
        {
          title: 'Upload Failed',
          icon: 'icon-triangle-warning',
          description: error instanceof AxiosError ? error.response?.data?.detail || error.message : 'Failed to upload ENM data.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      setEnmFile(null);
    } finally {
      setEnmUploading(false);
    }
  };

  const handleDownloadScript = () => {
    const link = document.createElement('a');
    link.href = collectEnmScriptUrl;
    link.download = 'collect_enm_data.sh';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const request: CreateRunsRequest = {
        runs: selectedRunTypes.map((type) => ({
          type,
          name: `${selectedRelease!.release_id} ${type === NiiRunType.FEATURE_IMPACT ? 'Feature Impact' : 'TR Impact'}`,
          release: {
            name: selectedRelease!.release_id,
            identity: selectedRelease!.library_identity
          },
          dataSource:
            dataSourceMode === 'enm_live'
              ? { kind: 'enm_live' as const, enmEndpointIds: selectedEnmIds }
              : dataSourceMode === 'epp'
                ? { kind: 'epp' as const, enmEndpointIds: selectedEppIds }
                : { kind: 'upload' as const, uploadId: enmUploadResult!.uploadId }
        }))
      };
      await niiService.createRuns(request);
      onSubmit();
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: error instanceof AxiosError
            ? error.response?.data?.message || error.message
            : 'Failed to trigger report generation.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setSubmitting(false);
    }
  };

  // Validation
  const dataSourceValid =
    dataSourceMode === 'enm_live'
      ? selectedEnmIds.length > 0
      : dataSourceMode === 'epp'
        ? selectedEppIds.length > 0
        : enmUploadResult !== null;
  const canSubmit = selectedRelease !== null && selectedRunTypes.length > 0 && dataSourceValid && !submitting;

  return ReactDOM.createPortal(
    <ReactEDSWrapper
      component={Dialog}
      show={open}
      onClose={onClose}
      title="New Impact Report"
      bottom={
        <>
          <ReactEDSWrapper component={Button} primary onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Generating…' : 'Generate'}
          </ReactEDSWrapper>
        </>
      }
    >
      {open && (
        <div className={styles.dialogContent}>
          {/* Release Selection */}
          <div className={styles.formSection}>
            <label className={styles.sectionLabel}>Release Name</label>
            <p className={styles.sectionHint}>Select a release from the available list.</p>
            {releasesLoading ? (
              <>
                <ReactEDSWrapper component={Select} label="Loading releases..." value="" disabled>
                  <ReactEDSWrapper component={MenuItem} value="">
                    Loading...
                  </ReactEDSWrapper>
                </ReactEDSWrapper>
                <p className={styles.sectionHint} style={{ marginTop: '8px', marginBottom: 0 }}>
                  This may take up to a minute. Please wait...
                </p>
              </>
            ) : availableReleases.length > 0 ? (
              <ReactEDSWrapper
                component={Select}
                type="input-single"
                label="Select a release"
                value={selectedRelease?.library_identity || ''}
                onChange={(raw: unknown) => {
                  const evt = raw as { target?: { value?: string } };
                  const value = evt?.target?.value ?? (raw as string);
                  const identity = Array.isArray(value) ? value[0] : value;
                  const release = availableReleases.find((r) => r.library_identity === identity);
                  setSelectedRelease(release || null);
                }}
              >
                {availableReleases.map((release) => (
                  <ReactEDSWrapper key={release.library_identity} component={MenuItem} value={release.library_identity}>
                    {`${release.release_id} — ${release.date}`}
                  </ReactEDSWrapper>
                ))}
              </ReactEDSWrapper>
            ) : (
              <ReactEDSWrapper component={Select} label="No releases available" value="" disabled>
                <ReactEDSWrapper component={MenuItem} value="">
                  No releases found
                </ReactEDSWrapper>
              </ReactEDSWrapper>
            )}
          </div>

          {/* Report Types */}
          <div className={styles.formSection}>
            <label className={styles.sectionLabel}>Select Report Type</label>
            <div className={styles.checkboxGroup}>
              <ReactEDSWrapper
                component={Checkbox}
                id="cb-feature-impact"
                checked={selectedRunTypes.includes(NiiRunType.FEATURE_IMPACT)}
                onChange={() => handleRunTypeToggle(NiiRunType.FEATURE_IMPACT)}
              >
                Feature Impact (Playbook)
              </ReactEDSWrapper>
              <ReactEDSWrapper
                component={Checkbox}
                id="cb-tr-impact"
                checked={selectedRunTypes.includes(NiiRunType.TR_IMPACT)}
                onChange={() => handleRunTypeToggle(NiiRunType.TR_IMPACT)}
              >
                Trouble Report Impact
              </ReactEDSWrapper>
            </div>
          </div>

          {/* Network Data Source */}
          <div className={styles.formSection}>
            <label className={styles.sectionLabel}>Network Data Source</label>
            <p className={styles.sectionHint}>Choose how to provide network inventory data.</p>
              <div className={styles.checkboxGroup}>
                <ReactEDSWrapper
                  component={RadioButton}
                  id="ds-enm-live"
                  name="dataSource"
                  value="enm_live"
                  checked={dataSourceMode === 'enm_live'}
                  onChange={() => setDataSourceMode('enm_live')}
                >
                  ENM Live Connection
                </ReactEDSWrapper>
                <ReactEDSWrapper
                  component={RadioButton}
                  id="ds-enm-upload"
                  name="dataSource"
                  value="enm_upload"
                  checked={dataSourceMode === 'enm_upload'}
                  onChange={() => setDataSourceMode('enm_upload')}
                >
                  Upload ENM Data File
                </ReactEDSWrapper>
                {eppConfig.enabled && (
                  <ReactEDSWrapper
                    component={RadioButton}
                    id="ds-epp"
                    name="dataSource"
                    value="epp"
                    checked={dataSourceMode === 'epp'}
                    onChange={() => setDataSourceMode('epp')}
                  >
                    EPP ENM Endpoint
                  </ReactEDSWrapper>
                )}
              </div>

              {/* ENM Live Selection */}
              {dataSourceMode === 'enm_live' && (
                <div style={{ marginTop: '12px' }}>
                  {enmsLoading && (
                    <div className={styles.loadingContainer}>
                      <div className="loader small" />
                      <p>Loading ENMs...</p>
                    </div>
                  )}
                  {!enmsLoading && enms.length > 0 && (
                    <ReactEDSWrapper
                      component={Select}
                      type="multi"
                      label="Select ENM"
                      value={selectedEnmIds}
                      onChange={(raw: unknown) => {
                        const evt = raw as { target?: { value?: string[] } };
                        const value = evt?.target?.value ?? (raw as string[]);
                        const selected = Array.isArray(value) ? value : [value];
                        setSelectedEnmIds(selected.filter(Boolean) as string[]);
                      }}
                    >
                      {enms.map((enm) => (
                        <ReactEDSWrapper key={enm.id} component={MenuItem} value={enm.id}>
                          {enm.name}
                        </ReactEDSWrapper>
                      ))}
                    </ReactEDSWrapper>
                  )}
                  {!enmsLoading && enms.length === 0 && <p className={styles.emptyMessage}>No ENM endpoints available.</p>}
                </div>
              )}

              {/* ENM Upload */}
              {dataSourceMode === 'enm_upload' && (
                <div style={{ marginTop: '12px' }}>
                  <a
                    role="button"
                    onClick={handleDownloadScript}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#0069c2', cursor: 'pointer', marginBottom: '12px' }}
                  >
                    <i className='icon icon-download-save'></i> Download ENM Script
                  </a>
                  <p className={styles.sectionHint}>Upload ENM data export (.zip or .json).</p>
                  <label className={styles.uploadBtn || ''} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px 16px', border: '1px solid #878787', borderRadius: '4px' }}>
                    <i className="icon icon-upload" />
                    {enmFile ? enmFile.name : 'Choose file...'}
                    <input
                      type="file"
                      accept=".zip,.json"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleEnmFileUpload(file);
                      }}
                      disabled={enmUploading}
                    />
                  </label>
                  {enmUploading && (
                    <p className={styles.sectionHint} style={{ marginTop: '8px' }}>
                      Uploading and validating...
                    </p>
                  )}
                  {enmUploadResult && (
                    <p style={{ marginTop: '8px', color: '#28a745' }}>
                      ✓ {enmUploadResult.validation?.featureCount} features, {enmUploadResult.validation?.hardwareCount} hardware entries detected
                    </p>
                  )}
                </div>
              )}

              {/* EPP ENM Selection (only when EPP enabled for this environment) */}
              {eppConfig.enabled && dataSourceMode === 'epp' && (
                <div style={{ marginTop: '12px' }}>
                  {eppLoading && (
                    <div className={styles.loadingContainer}>
                      <div className="loader small" />
                      <p>Loading EPP ENMs...</p>
                    </div>
                  )}
                  {!eppLoading && eppEnms.length > 0 && (
                    <ReactEDSWrapper
                      component={Select}
                      type="multi"
                      label="Select EPP ENM"
                      value={selectedEppIds}
                      onChange={(raw: unknown) => {
                        const evt = raw as { target?: { value?: string[] } };
                        const value = evt?.target?.value ?? (raw as string[]);
                        const selected = Array.isArray(value) ? value : [value];
                        setSelectedEppIds(selected.filter(Boolean) as string[]);
                      }}
                    >
                      {eppEnms.map((ep) => (
                        <ReactEDSWrapper key={ep.id} component={MenuItem} value={ep.id}>
                          {ep.name}
                        </ReactEDSWrapper>
                      ))}
                    </ReactEDSWrapper>
                  )}
                  {!eppLoading && eppEnms.length === 0 && (
                    <p className={styles.emptyMessage}>No EPP ENM endpoints. Create one on the ENMs page.</p>
                  )}
                </div>
              )}
            </div>
        </div>
      )}
    </ReactEDSWrapper>,
    document.body
  );
};

export default GenerateReportDialog;
